/**
 * The Rules page of the account (§5.27, Claude Design R10) — a workspace's
 * rules repository as GitHub holds it, read and changed through the
 * organization's Airlock GitHub App (`rules-routes.ts`). An organization names
 * no rules repository of its own: its Rules page is the figures alone
 * (`OrgRulesPage`).
 *
 * The head says which repository, which branch and which commit was read. What
 * the account cannot reach is a state, each with what to do: no repository
 * named (where to name it), the App not installed (who installs it, where),
 * installed but not on that repository, a repository elsewhere than GitHub
 * (browse it in the app). Below: a search and the level and status filters,
 * the RFCs by folder on the left — the top first, then each folder —, the one
 * chosen on the right, read and changed in the same place (`rules-sheet.tsx`,
 * Claude Design kit 67 on the account's components).
 *
 * Changing: whoever has write on the workspace keeps changes — a block, a
 * statement removed, an RFC dropped on another folder, a statement dropped on
 * another RFC — in a draft kept in this browser, then opens one pull request
 * with all of it — authored by them, opened by the App, never written to the
 * branch. A member without that right reads only, and the page says so.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import type React from 'react';
import { RULE_LEVELS, RULE_STATUSES, type RuleRfc } from '@airlock/contracts/rules/document';
import type { AccountRulesPullResult, AccountRulesView } from '@airlock/contracts/rules/account';
import { ruleFileName } from '@airlock/contracts/rules/repository';
import { ListEmpty, Note, PageBar, PageBody } from '../../../components/list';
import { Modal, useConfirm } from '../../../components/Modal';
import { GhostButton, Input, PrimaryButton } from '../../../components/primitives';
import { api, apiPath, prefix } from '../../../lib/api';
import { color, font } from '../../../theme/tokens';
import { failureText, textareaStyle } from '../shared';
import { RulesListRow } from './rules-parts';
import { RulesSheet, type KeepItem } from './rules-sheet';
import { RulesFolders } from './rules-folders';
import {
  changesOf,
  deleteRfc,
  draftKey,
  dropChange,
  keepRfc,
  loadDraft,
  moveStatement,
  nextFree,
  pullText,
  saveDraft,
  statementMatches,
  undoStatement,
  withDraft,
  type RulesDraft,
  type StatementMove
} from './rules-draft';

export function RulesPage({
  orgId,
  orgName,
  workspaceId,
  onGo
}: {
  orgId: string;
  orgName: string;
  workspaceId: string;
  /** To the page where the repository is named. */
  onGo: () => void;
}): React.JSX.Element {
  const base = apiPath`/orgs/${orgId}/rules/workspaces/${workspaceId}`;
  const origin = 'ws';
  const label = 'Workspace rules';
  const [view, setView] = useState<AccountRulesView | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [draft, setDraft] = useState<RulesDraft>({ base: null, changes: [] });
  const [selected, setSelected] = useState<number | null>(null);
  const [fresh, setFresh] = useState<RuleRfc | null>(null);
  const [dirty, setDirty] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [levels, setLevels] = useState<string[]>([]);
  const [statuses, setStatuses] = useState<string[]>([]);
  const [proposing, setProposing] = useState<{ title: string; body: string } | null>(null);
  const [sending, setSending] = useState(false);
  const [sendFailure, setSendFailure] = useState<string | null>(null);
  const [opened, setOpened] = useState<AccountRulesPullResult | null>(null);
  const [dialog, ask] = useConfirm();

  const load = useCallback(async (): Promise<void> => {
    setFailure(null);
    try {
      setView(await api.get<AccountRulesView>(base));
    } catch (error) {
      setFailure(failureText(error));
    }
  }, [base]);
  useEffect(() => void load(), [load]);

  const key = view?.repository ? draftKey(orgId, workspaceId, view.repository.remote) : null;
  useEffect(() => setDraft(key ? loadDraft(key) : { base: null, changes: [] }), [key]);
  const change = (next: RulesDraft): void => {
    setDraft(next);
    if (key) saveDraft(key, next);
  };

  const rfcs = useMemo(() => view?.read?.rfcs ?? [], [view]);
  const listed = useMemo(() => withDraft(rfcs, draft), [rfcs, draft]);
  const filtering = q.trim() !== '' || levels.length > 0 || statuses.length > 0;
  const shown = filtering ? listed.filter(({ rfc }) => rfc.statements.some((one) => statementMatches(rfc, one, q, levels, statuses))) : listed;
  const chosen = shown.find(({ rfc }) => rfc.rfc === selected) ?? shown[0] ?? null;
  const canWrite = view?.canWrite === true && view.app === 'ok' && view.read !== null;
  const sheet: RuleRfc | null = fresh ?? chosen?.rfc ?? null;

  if (failure) {
    return (
      <PageBody>
        <Note tone="warn">{failure}</Note>
        <div>
          <GhostButton size="sm" onClick={() => void load()}>
            Try again
          </GhostButton>
        </div>
      </PageBody>
    );
  }
  if (!view) {
    return (
      <PageBody>
        <p style={{ margin: 0, fontSize: 13, color: color.inkFaint }}>Reading the rules repository on GitHub…</p>
      </PageBody>
    );
  }
  if (!view.repository) {
    return (
      <ListEmpty title="No rules repository is named">
        Name one on this workspace’s Projects page, under Rules repository: its rules then hold in this workspace.{' '}
        <button type="button" onClick={onGo} style={linkButton}>
          Open Projects
        </button>
      </ListEmpty>
    );
  }

  const remote = view.repository.remote;
  const head = (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: '4px 10px', minWidth: 0 }}>
      <span style={{ fontFamily: font.mono, fontSize: 10.5, letterSpacing: '.12em', color: color.inkFaint }}>{label.toUpperCase()}</span>
      <span style={{ fontFamily: font.mono, fontSize: 12.5, color: color.inkMuted, overflowWrap: 'anywhere' }}>
        {[remote, view.read?.branch ?? view.repository.branch ?? 'default branch', ...(view.read ? [view.read.commit.slice(0, 7)] : [])].join(' · ')}
      </span>
    </div>
  );

  const unreachable =
    view.app === 'not-github' ? (
      <Note>The account reads rules repositories on GitHub only — browse this one in the app.</Note>
    ) : view.app === 'not-configured' ? (
      <Note>This deployment has no GitHub App, so the account cannot read {remote}. Browse it in the app.</Note>
    ) : view.app === 'not-installed' ? (
      <Note tone="warn">
        This page reads {remote} through the organization’s Airlock GitHub App, and {orgName} has not installed it. An owner or an administrator installs it
        under Integrations › GitHub{view.installUrl ? ' — or on GitHub directly' : ''}.
        {view.installUrl && (
          <>
            {' '}
            <a href={view.installUrl} target="_blank" rel="noreferrer" style={{ color: color.accentText }}>
              Install the App
            </a>
          </>
        )}
      </Note>
    ) : view.app === 'out-of-reach' ? (
      <Note tone="warn">
        The organization’s Airlock GitHub App is installed, but not on {remote}: {view.error} Add the repository to the App’s installation on GitHub.
        {view.installUrl && (
          <>
            {' '}
            <a href={view.installUrl} target="_blank" rel="noreferrer" style={{ color: color.accentText }}>
              Open the App on GitHub
            </a>
          </>
        )}
      </Note>
    ) : view.error ? (
      <Note tone="warn">Could not read {remote}: {view.error}</Note>
    ) : null;

  const branchOf = (rfc: number): RuleRfc | null => rfcs.find((one) => one.rfc === rfc) ?? null;
  const docOf = (rfc: number): RuleRfc | null => listed.find((one) => one.rfc.rfc === rfc && one.mark !== 'deleted')?.rfc ?? null;
  const movesOf = (rfc: number): StatementMove[] => {
    const kept = draft.changes.find((one) => one.rfc === rfc);
    return kept?.kind === 'put' ? (kept.moved ?? []) : [];
  };
  const keep = (items: KeepItem[]): void => {
    let next = draft;
    for (const item of items) next = keepRfc(next, item.doc, branchOf(item.doc.rfc), view.read?.commit ?? null, item.checks ?? {}, item.moved);
    change(next);
    setOpened(null);
  };
  const moveRfc = (rfc: number, repos: string[]): void => {
    const doc = docOf(rfc);
    if (!doc) return;
    const { repos: _repos, ...rest } = doc;
    keep([{ doc: repos.length ? { ...rest, repos: [...repos].sort() } : rest }]);
  };
  /** A statement dropped onto another RFC — kept at once, or why it cannot land there. */
  const moveOne = (fromRfc: number, slug: string, toRfc: number): string | null => {
    const a = docOf(fromRfc);
    const b = docOf(toRfc);
    if (!a || !b) return 'That RFC cannot take it.';
    const moved = moveStatement(a, b, slug, origin);
    if ('refused' in moved) return moved.refused;
    const add = (rfc: number): StatementMove[] => [...movesOf(rfc).filter((one) => one.slug !== slug), moved.move];
    keep([
      { doc: moved.from, moved: add(fromRfc) },
      { doc: moved.to, moved: add(toRfc) }
    ]);
    return null;
  };
  const undoMove = (fromRfc: number, slug: string): void => {
    const move = movesOf(fromRfc).find((one) => one.slug === slug && one.from === fromRfc);
    const a = docOf(fromRfc);
    const b = move ? docOf(move.to) : null;
    if (!move || !a || !b) return;
    const without = (rfc: number): StatementMove[] => movesOf(rfc).filter((one) => !(one.slug === slug && one.from === fromRfc));
    keep([
      { doc: undoStatement(branchOf(fromRfc), a, slug), moved: without(fromRfc) },
      { doc: undoStatement(branchOf(move.to), b, slug), moved: without(move.to) }
    ]);
  };
  /** Somewhere else — asked first while a block is changed and not kept. */
  const leave = async (go: () => void): Promise<void> => {
    if (dirty) {
      const yes = await ask({
        title: `Leave ${dirty} with changes not kept?`,
        lede: `What you changed in ${dirty} is dropped. The draft keeps what was kept before.`,
        confirm: 'Discard',
        danger: true
      });
      if (!yes) return;
      setDirty(null);
    }
    go();
  };
  const remove = async (rfc: RuleRfc): Promise<void> => {
    const onBranch = rfcs.some((one) => one.rfc === rfc.rfc);
    const yes = await ask({
      title: `Delete ${origin}:${rfc.rfc} · ${rfc.title}?`,
      lede: onBranch
        ? `Its file is deleted in the pull request you open next; its ${rfc.statements.length} rules apply until it is merged.`
        : 'It leaves this draft; the repository never held it.',
      confirm: 'Delete RFC',
      danger: true
    });
    if (!yes) return;
    change(deleteRfc(draft, rfc.rfc, rfc.title, onBranch, view.read?.commit ?? null));
    setDirty(null);
  };
  const send = async (): Promise<void> => {
    if (!proposing) return;
    setSending(true);
    setSendFailure(null);
    try {
      const result = await api.post<AccountRulesPullResult>(apiPath`${prefix(base)}/pull`, { changes: changesOf(draft), title: proposing.title, body: proposing.body, base: draft.base ?? '' });
      change({ base: null, changes: [] });
      setProposing(null);
      setOpened(result);
    } catch (error) {
      setSendFailure(failureText(error));
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {dialog}
      <PageBar>
        <Input
          type="search"
          value={q}
          placeholder="Search statements, ids, why"
          onChange={(event) => setQ(event.target.value)}
          style={{ maxWidth: 320, padding: '8px 11px', fontSize: 13 }}
        />
        <Chips label="Level" all={RULE_LEVELS} picked={levels} onPick={setLevels} />
        <Chips label="Status" all={RULE_STATUSES} picked={statuses} onPick={setStatuses} />
        <span style={{ flex: 1 }} />
        {canWrite && (
          <GhostButton size="sm" disabled={!!dirty} onClick={() => void leave(() => setFresh(blank(nextFree(rfcs, draft), [])))}>
            + New RFC
          </GhostButton>
        )}
      </PageBar>
      <PageBody gap={14}>
        {head}
        {unreachable}
        {view.read?.problems.map((problem) => (
          <Note key={problem} tone="warn">
            {problem}
          </Note>
        ))}
        {view.app === 'ok' && view.read && !view.canWrite && (
          <Note>
            You read these rules. Changing them asks for write on this workspace.{' '}
            In the app, anyone who may push to {remote} proposes a change too.
          </Note>
        )}
        {canWrite && draft.changes.length > 0 && (
          <div style={band}>
            <span style={{ fontFamily: font.mono, fontSize: 10.5, letterSpacing: '.12em', color: color.warnText }}>DRAFT</span>
            <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Steps at={dirty ? 0 : 2} />
              <span style={{ fontSize: 13, color: color.inkBody }}>
                {draft.changes.length} change{draft.changes.length === 1 ? '' : 's'} in this browser — nothing applies before the pull request is merged.
              </span>
            </span>
            <GhostButton size="sm" disabled={!!dirty} onClick={() => change({ base: null, changes: [] })}>
              Discard
            </GhostButton>
            <PrimaryButton size="sm" disabled={!!dirty} reason={dirty ? `Keep or cancel ${dirty} first.` : undefined} onClick={() => setProposing(pullText(draft, rfcs, origin))}>
              Open pull request
            </PrimaryButton>
          </div>
        )}
        {opened && (
          <Note tone="teal">
            Pull request{' '}
            <a href={opened.url} target="_blank" rel="noreferrer" style={{ color: color.accentText, fontWeight: 600 }}>
              #{opened.number}
            </a>{' '}
            opened on {remote} from {opened.branch}. Its changes apply once it is merged; until then the rules are as on {view.read?.branch}.
          </Note>
        )}

        {view.read ? (
          listed.length === 0 && !fresh ? (
            <ListEmpty title={`${remote} holds no RFC yet`}>
              {canWrite ? 'Write the first one with New RFC: it reaches the repository as a pull request.' : 'Its RFCs are JSON files, 0001-<slug>.json, at its top or in a folder named after the projects they hold in.'}
            </ListEmpty>
          ) : (
            <div className="al-rules" style={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 320px) minmax(0, 1fr)', gap: 18, alignItems: 'start' }}>
              <RulesFolders
                listed={shown}
                canWrite={canWrite && !dirty}
                origin={origin}
                selected={fresh ? null : (chosen?.rfc.rfc ?? null)}
                count={(rfc) => (filtering ? rfc.statements.filter((one) => statementMatches(rfc, one, q, levels, statuses)).length : null)}
                onSelect={(rfc) => void leave(() => (setFresh(null), setSelected(rfc)))}
                onNew={(repos) => void leave(() => setFresh(blank(nextFree(rfcs, draft), repos)))}
                onMoveRfc={moveRfc}
                onMoveStatement={moveOne}
                empty={shown.length === 0 ? 'No rule matches.' : null}
                Row={RulesListRow}
              />
              {sheet ? (
                <RulesSheet
                  key={fresh ? `fresh-${fresh.rfc}` : sheet.rfc}
                  origin={origin}
                  branch={fresh ? null : branchOf(sheet.rfc)}
                  change={fresh ? null : (draft.changes.find((one) => one.rfc === sheet.rfc) ?? null)}
                  current={sheet}
                  fresh={!!fresh}
                  folders={listed.map((one) => one.rfc.repos ?? [])}
                  canWrite={canWrite}
                  q={q}
                  matches={(one) => !filtering || statementMatches(sheet, one, q, levels, statuses)}
                  heldScript={(path) => {
                    const drafted = draft.changes.find((one) => one.rfc === sheet.rfc);
                    return (drafted?.kind === 'put' ? drafted.checks?.[path] : undefined) ?? view.read?.checks?.[path];
                  }}
                  onKeep={keep}
                  onUndoRfc={() => change(dropChange(draft, sheet.rfc))}
                  onUndoMove={(slug) => undoMove(sheet.rfc, slug)}
                  onDelete={() => void remove(sheet)}
                  onFreshDone={(rfc) => {
                    setFresh(null);
                    setDirty(null);
                    if (rfc !== null) setSelected(rfc);
                  }}
                  onDirty={setDirty}
                  grip={(slug) => <Grip rfc={sheet.rfc} slug={slug} />}
                />
              ) : null}
            </div>
          )
        ) : null}
      </PageBody>

      {proposing && (
        <Modal
          title="Open a pull request"
          lede={`On ${remote}, into ${view.read?.branch ?? 'its default branch'}, from a branch of its own. You are its author; the organization’s Airlock GitHub App opens it.`}
          confirm="Open pull request"
          busy={sending}
          problem={sendFailure ?? undefined}
          foot="Your draft is kept until GitHub opens it."
          width={560}
          onConfirm={() => void send()}
          onCancel={() => {
            setProposing(null);
            setSendFailure(null);
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginBottom: 12, fontFamily: font.mono, fontSize: 12, color: color.inkMuted }}>
            <span style={{ fontSize: 10.5, letterSpacing: '.12em', color: color.inkFaint }}>FILES</span>
            {filesOf(draft, rfcs).map((one) => (
              <span key={one} style={{ overflowWrap: 'anywhere' }}>
                {one}
              </span>
            ))}
          </div>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: 12.5, color: color.inkMuted }}>Title</span>
            <Input value={proposing.title} onChange={(event) => setProposing({ ...proposing, title: event.target.value })} />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 12 }}>
            <span style={{ fontSize: 12.5, color: color.inkMuted }}>Description</span>
            <textarea rows={6} value={proposing.body} onChange={(event) => setProposing({ ...proposing, body: event.target.value })} style={textareaStyle} />
          </label>
        </Modal>
      )}
    </>
  );
}

/** A filter's values as toggles: none picked is every one. */
function Chips({ label, all, picked, onPick }: { label: string; all: readonly string[]; picked: string[]; onPick: (next: string[]) => void }): React.JSX.Element {
  return (
    <div role="group" aria-label={label} style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
      {all.map((value) => {
        const on = picked.includes(value);
        return (
          <button
            key={value}
            type="button"
            aria-pressed={on}
            onClick={() => onPick(on ? picked.filter((one) => one !== value) : [...picked, value])}
            style={{
              padding: '4px 9px',
              border: `1px solid ${on ? color.accentRule : color.lineInput}`,
              borderRadius: 999,
              background: on ? color.accentWash : color.surface,
              color: on ? color.accentText : color.inkMuted,
              fontFamily: label === 'Level' ? font.mono : 'inherit',
              fontSize: 11.5,
              whiteSpace: 'nowrap',
              cursor: 'pointer'
            }}
          >
            {value}
          </button>
        );
      })}
    </div>
  );
}

function blank(rfc: number, repos: string[]): RuleRfc {
  return { rfc, slug: '', title: '', status: 'draft', summary: '', statements: [{ slug: '', level: 'MUST', text: '' }], ...(repos.length ? { repos } : {}) };
}

const STEPS = ['EDITED', 'KEPT IN THIS BROWSER', 'PULL REQUEST', 'APPLIED ONCE MERGED'];

/** Where the change stands: the steps done, the one that is next, the ones after. */
function Steps({ at }: { at: number }): React.JSX.Element {
  return (
    <span style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 8px', fontFamily: font.mono, fontSize: 10.5, letterSpacing: '.06em' }}>
      {STEPS.map((step, index) => (
        <span key={step} aria-current={index === at ? 'step' : undefined} style={{ color: index < at ? color.inkFaint : index === at ? color.ink : color.inkGhost, fontWeight: index === at ? 600 : 400 }}>
          {index ? '→ ' : ''}
          {index < at ? '✓ ' : ''}
          {step}
        </span>
      ))}
    </span>
  );
}

/** What the pull request writes, a file a line: − removed, + written, ~ rewritten. */
function filesOf(draft: RulesDraft, rfcs: readonly RuleRfc[]): string[] {
  const out: string[] = [];
  for (const one of draft.changes) {
    const read = rfcs.find((rfc) => rfc.rfc === one.rfc);
    const was = read ? ruleFileName(read.rfc, read.slug, read.repos ?? []) : null;
    if (one.kind === 'delete') {
      if (was) out.push(`− ${was}`);
      continue;
    }
    const now = ruleFileName(one.doc.rfc, one.doc.slug, one.doc.repos ?? []);
    if (was && was !== now) out.push(`− ${was}`, `+ ${now}`);
    else out.push(`${was ? '~' : '+'} ${now}`);
    for (const path of Object.keys(one.checks ?? {})) out.push(`~ ${path}`);
  }
  return [...new Set(out)];
}

/** A statement's grip: dragged onto an RFC of the list, the statement moves there (kit 67). */
function Grip({ rfc, slug }: { rfc: number; slug: string }): React.JSX.Element {
  return (
    <span
      draggable
      role="img"
      title="Drag onto another RFC in the list"
      aria-label={`Move ${slug} to another RFC`}
      onDragStart={(event) => {
        event.dataTransfer.setData('application/x-airlock-rule', JSON.stringify({ kind: 'statement', rfc, slug }));
        event.dataTransfer.effectAllowed = 'move';
      }}
      style={{ cursor: 'grab', color: color.inkGhost, fontSize: 12, lineHeight: 1, userSelect: 'none' }}
    >
      ⠿
    </span>
  );
}


const band: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: 10,
  padding: '9px 12px',
  background: color.warnBg,
  boxShadow: `inset 2px 0 0 ${color.yellow}`
};

const linkButton: React.CSSProperties = {
  padding: 0,
  border: 0,
  background: 'none',
  color: color.accentText,
  font: 'inherit',
  cursor: 'pointer',
  textDecoration: 'underline'
};
